import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ArrowDown, ArrowUp, Check, EyeOff, GripVertical, Pencil, Plus, RotateCcw } from "lucide-react";
import { Button, Card, CardBody, CardHeader, ErrorState, IconButton, PageHeader } from "@novemai/components";
import { reconcileHomeLayout, type HomeWidgetId, type HomeWidgetLayout, type HomeWidgetSize } from "@novemai/reports-sdk";
import { EnvelopeError, errorMessage } from "@/api/errors";
import { useRetainedCommand } from "@/api/use-retained-command";
import { useSessionScope } from "@/auth/use-session-scope";
import { menuLinks } from "@/components/menu-config";
import { useVisibleMenu } from "@/components/use-visible-menu";
import { cn } from "@/lib/utils";
import { fetchHomeLayout, saveHomeLayout, type HomeLayoutState, type SaveHomeLayout } from "./api";

export interface HomeWidget { readonly id: HomeWidgetId; readonly label: string; readonly content: ReactNode }
interface Props {
  readonly title: ReactNode;
  readonly description: string;
  readonly widgets: readonly HomeWidget[];
  /** Sayfanın ana eylemi; düzenleme sırasında kaydet/vazgeç ile yer değiştirir. */
  readonly primaryAction?: ReactNode;
}
const WIDTHS: Record<HomeWidgetSize, string> = { small: "xl:col-span-3", medium: "xl:col-span-6", large: "xl:col-span-12" };

/** Düzen kişisel tercihtir; başka oturuma ait taslak yeni kimlikle kaydedilemez. */
export function HomeDashboard(props: Props): React.ReactElement {
  const session = useSessionScope();
  return <HomeDashboardSession key={JSON.stringify(session)} {...props} session={session} />;
}

function HomeDashboardSession({ title, description, widgets, primaryAction, session }: Props & { readonly session: readonly string[] }): React.ReactElement {
  const { t } = useTranslation();
  const client = useQueryClient();
  const queryKey = ["home-dashboard", ...session];
  const saved = useQuery({ queryKey, queryFn: fetchHomeLayout, retry: false });
  const [draft, setDraft] = useState<SaveHomeLayout | null>(null);
  const [dragged, setDragged] = useState<HomeWidgetId | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const command = useRetainedCommand<SaveHomeLayout, HomeLayoutState>({ key: ["home-dashboard"], execute: saveHomeLayout,
    onSuccess: row => { client.setQueryData(queryKey, row); setDraft(null); setSavedNotice(true); } });
  const retained = command.frozen ? command.body : undefined;
  const editing = draft !== null || retained !== undefined;
  const current = retained ?? draft;
  const layout = reconcileHomeLayout(current ? current.layout : saved.data?.layout ?? null);
  const available = new Map(widgets.map(widget => [widget.id, widget]));
  const visible = layout.filter(item => !item.hidden && available.has(item.id));
  const hidden = layout.filter(item => item.hidden && available.has(item.id));
  const conflict = command.error instanceof EnvelopeError && command.error.hasCode("employee.home_dashboard_conflict");

  function change(next: readonly HomeWidgetLayout[]): void {
    if (command.frozen || current === null || current === undefined) return;
    setDraft({ ...current, layout: [...next] });
    setSavedNotice(false);
  }
  function move(id: HomeWidgetId, target: HomeWidgetId): void {
    if (id === target) return;
    const next = [...layout];
    const from = next.findIndex(item => item.id === id);
    const to = next.findIndex(item => item.id === target);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    if (moved !== undefined) next.splice(to, 0, moved);
    change(next);
  }
  async function reload(): Promise<void> {
    if (!command.reset()) return;
    const result = await saved.refetch();
    if (result.isSuccess) { setDraft(null); setSavedNotice(false); }
  }

  return <>
    <PageHeader title={title} description={description} actions={<div className="flex flex-wrap justify-end gap-2">
      {editing ? <>
        <Button variant="ghost" disabled={command.frozen} onClick={() => { if (current) setDraft({ ...current, layout: null }); }}><RotateCcw size={15} />{t("home_dashboard.reset")}</Button>
        <Button variant="secondary" disabled={command.frozen} onClick={() => { if (command.reset()) setDraft(null); }}>{t("home_dashboard.cancel")}</Button>
        <Button disabled={command.frozen || conflict || current === null || current === undefined} onClick={() => { if (current) void command.send(current); }}><Check size={15} />{t("home_dashboard.save")}</Button>
      </> : <Button variant="secondary" disabled={saved.data === undefined || saved.isError} onClick={() => {
        if (!saved.data) return;
        command.reset(); setDraft({ layout: [...reconcileHomeLayout(saved.data.layout)], expected_revision: saved.data.revision }); setSavedNotice(false);
      }}><Pencil size={15} />{t("home_dashboard.edit")}</Button>}
      {!editing && primaryAction}
    </div>} />

    {saved.isError && <div className="mb-5"><ErrorState title={t("home_dashboard.load_error")} description={errorMessage(saved.error, t)} /><Button variant="secondary" onClick={() => void saved.refetch()}>{t("home_dashboard.retry")}</Button></div>}
    {savedNotice && <p role="status" className="mb-4 text-sm text-[var(--fg-secondary)]">{t("home_dashboard.saved")}</p>}
    {editing && <div className="mb-5 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-4">
      <p className="text-sm font-semibold text-[var(--fg-primary)]">{t("home_dashboard.edit_title")}</p>
      <p className="mt-1 text-sm text-[var(--fg-secondary)]">{t("home_dashboard.edit_hint")}</p>
      {command.frozen && <div role="status" className="mt-3 text-sm text-[var(--fg-primary)]">{t(command.busy ? "home_dashboard.saving" : "home_dashboard.uncertain")}</div>}
      {command.error !== null && <div className="mt-3" role="alert">
        <p className="text-sm text-[var(--fg-primary)]">{conflict ? t("home_dashboard.conflict") : errorMessage(command.error, t)}</p>
        {command.frozen ? <Button className="mt-2" variant="secondary" disabled={command.busy} onClick={() => void command.retry()}>{t("home_dashboard.retry_same")}</Button>
          : conflict ? <Button className="mt-2" variant="secondary" onClick={() => void reload()}>{t("home_dashboard.reload")}</Button> : null}
      </div>}
      {hidden.length > 0 && <div className="mt-4 flex flex-wrap items-center gap-2" aria-label={t("home_dashboard.library")}>
        <span className="mr-1 text-sm text-[var(--fg-secondary)]">{t("home_dashboard.library")}</span>
        {hidden.map(item => <Button key={item.id} variant="secondary" disabled={command.frozen} onClick={() => change(layout.map(widget => widget.id === item.id ? { ...widget, hidden: false } : widget))}><Plus size={14} />{available.get(item.id)?.label}</Button>)}
      </div>}
    </div>}

    <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-12" data-testid="home-widget-grid">
      {visible.map((item, index) => {
        const widget = available.get(item.id)!;
        return <section key={item.id} aria-label={widget.label} data-widget-id={item.id} data-widget-size={item.size}
          className={cn("min-w-0", WIDTHS[item.size], editing && "rounded-[var(--radius-lg)] border border-dashed border-[var(--border-strong)] p-2")}
          onDragOver={event => { if (dragged !== null && !command.frozen) event.preventDefault(); }}
          onDrop={event => { event.preventDefault(); if (dragged !== null) move(dragged, item.id); setDragged(null); }}>
          {editing && <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1">
              <span draggable={!command.frozen} onDragStart={event => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", item.id); setDragged(item.id); }} onDragEnd={() => setDragged(null)} className="cursor-grab p-1 text-[var(--fg-muted)]" aria-hidden="true"><GripVertical size={16} /></span>
              <span className="text-xs font-medium text-[var(--fg-secondary)]">{widget.label}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <IconButton aria-label={t("home_dashboard.move_up", { name: widget.label })} disabled={command.frozen || index === 0} onClick={() => { const target = visible[index - 1]; if (target) move(item.id, target.id); }}><ArrowUp size={14} /></IconButton>
              <IconButton aria-label={t("home_dashboard.move_down", { name: widget.label })} disabled={command.frozen || index === visible.length - 1} onClick={() => { const target = visible[index + 1]; if (target) move(item.id, target.id); }}><ArrowDown size={14} /></IconButton>
              <select aria-label={t("home_dashboard.size", { name: widget.label })} disabled={command.frozen} value={item.size}
                onChange={event => change(layout.map(widget => widget.id === item.id ? { ...widget, size: event.target.value as HomeWidgetSize } : widget))}
                className="min-w-0 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-1.5 text-xs text-[var(--fg-primary)]">
                <option value="small">{t("home_dashboard.small")}</option><option value="medium">{t("home_dashboard.medium")}</option><option value="large">{t("home_dashboard.large")}</option>
              </select>
              <IconButton aria-label={t("home_dashboard.hide", { name: widget.label })} disabled={command.frozen} onClick={() => change(layout.map(widget => widget.id === item.id ? { ...widget, hidden: true } : widget))}><EyeOff size={14} /></IconButton>
            </div>
          </div>}
          <div className="min-w-0 overflow-x-auto">{widget.content}</div>
        </section>;
      })}
    </div>
    {visible.length === 0 && <p className="rounded-[var(--radius-lg)] border border-dashed border-[var(--border-subtle)] p-8 text-center text-sm text-[var(--fg-secondary)]">{t("home_dashboard.empty")}</p>}
  </>;
}

// Çizimdeki "Hızlı aksiyonlar": rayın ilk sekiz girdisi değil, seçilmiş
// self-servis hedefleri. Rayda görünmeyen hedef burada da görünmez.
const QUICK_LINK_KEYS = [
  "me-requests", "me-leave-balance", "me-tasks", "me-payroll",
  "me-document-requests", "me-training", "me-surveys", "me-announcements",
] as const;

export function HomeQuickLinks(): React.ReactElement | null {
  const { t } = useTranslation();
  const menu = useVisibleMenu();
  const visible = new Map(menu.sections.flatMap(section => menuLinks(section.items)).map(item => [item.key, item]));
  const items = QUICK_LINK_KEYS.flatMap(key => { const item = visible.get(key); return item ? [item] : []; });
  if (items.length === 0) return null;
  return <Card><CardHeader><h2 className="nai-card-title">{t("home_dashboard.quick_links")}</h2></CardHeader><CardBody>
    <div className="grid gap-2 sm:grid-cols-2">{items.map(item => <Link key={item.key} to={item.href} className="flex items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-3 text-sm text-[var(--fg-primary)] hover:bg-[var(--bg-subtle)]"><item.icon size={16} aria-hidden="true" />{t(item.labelKey)}</Link>)}</div>
  </CardBody></Card>;
}
